import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styles from './FlyoutMenu.scss';
import FlyoutMenuItem from '../flyout-menu-item/FlyoutMenuItem';
import CloseButton from './CloseButton';
import formatTestId from '../../../utils/test-id/format-testid';
import notifyAnalytics from '../notify-analytics';

const WithClickableHeading = ({ url, text, hierarchy, testId, children }) =>
  url ? (
    <a
      href={url}
      alt={text}
      className={classNames(styles.flyoutMenu__heading__clickable)}
      onClick={() => notifyAnalytics(`${hierarchy}|${text}`)}
      data-testid={testId}
    >
      {children}
    </a>
  ) : (
    children
  );

const GroupedFlyoutMenu = ({ columns, hierarchy, onClose, isTouch }) => {
  const filteredColumns =
    columns
      ?.filter(column => column.isDisplayedInGlobalNav !== false)
      ?.map(column => ({
        ...column,
        child: column.child?.filter(
          item => item.isDisplayedInGlobalNav !== false
        ),
      })) || [];

  const groupByColumn = items =>
    Array.from({ length: 6 })
      .map((_, index) => items.filter(i => i.column === index + 1))
      .filter(c => c.length);

  const groups = groupByColumn(filteredColumns);
  return (
    <div
      className={styles.flyoutMenu}
      data-testid="desktop-flyout-grouped-menu"
    >
      <div className={styles.flyoutMenuColumns}>
        {groups.map((group, i) => (
          <ul
            key={`column-${i + 1}`}
            className={classNames(
              styles.flyoutMenu__column,
              styles.flyoutMenu__column__grouped
            )}
            data-testid={`desktop-flyout-column-${i + 1}`}
          >
            {group.map(category => {
              const { name: groupHeading, url: headingUrl, child } = category;
              return (
                <li key={groupHeading} className={styles.flyoutMenu__group}>
                  <WithClickableHeading
                    url={headingUrl}
                    text={groupHeading}
                    hierarchy={hierarchy}
                    testId={formatTestId(groupHeading, 'flyout-heading-link-')}
                    isTouch={isTouch}
                  >
                    <h4
                      className={classNames(
                        styles.flyoutMenuTitle,
                        styles.flyoutMenuTitle__no_underline
                      )}
                      data-testid={formatTestId(
                        groupHeading,
                        'flyout-heading-'
                      )}
                    >
                      {groupHeading}
                    </h4>
                  </WithClickableHeading>
                  {child && (
                    <ul>
                      {child.map(link => {
                        const { name: itemName, url: linkURL } = link;
                        return (
                          <FlyoutMenuItem
                            key={itemName}
                            label={itemName}
                            url={linkURL}
                            hierarchy={`${hierarchy}|${groupHeading}`}
                          />
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        ))}
      </div>
      {isTouch && <CloseButton onClose={onClose} />}
    </div>
  );
};

const itemShape = {
  name: PropTypes.string.isRequired,
  url: PropTypes.string,
};
itemShape.child = PropTypes.arrayOf(PropTypes.shape(itemShape));

GroupedFlyoutMenu.defaultProps = {
  isTouch: false,
};

GroupedFlyoutMenu.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape(itemShape)).isRequired,
  hierarchy: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  isTouch: PropTypes.bool,
};

export default GroupedFlyoutMenu;
